import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import {
  Copy,
  Check,
  Trash2,
  Loader2,
  Wrench,
  Cog,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
} from 'lucide-react';
import { StreamingMarkdownText } from '@/components/streaming-markdown-text';
import { cn } from '@/lib/utils';
import type { ChatMessageData } from './types';
import { NodeUpdateMessage } from './node-update-message';
import { WorkflowMutationMessage } from './workflow-mutation-message';
import { ToolCallRollup } from './tool-call-rollup';
import {
  ChatArtifactCards,
  promotedArtifactCards,
} from '@/components/chat-artifact-cards';
import { getFileIcon } from '@/constants/node-styling';
import type { ArtifactListItem } from '@/types/artifacts';
import { CollectionChips } from './collection-chips';
import { parseCollectionTags } from '@/utils/collection-references';
export interface ClarificationAnswerControl {
  /**
   * Deliver this card's answer to the BE.  Returns ``true`` on success
   * (host optimistically flipped the card to ``'answered'`` + stashed
   * ``userAnswer``) and ``false`` on failure (host restored the prior
   * state so the user can retry).
   *
   * ``declined`` is the "Skip" path: the host flips the card to
   * ``skipped`` and the BE uses the same no-answer re-dispatch
   * semantics as a timeout (but broadcasts ``skipped=true`` so the
   * transcript reads "Skipped").  ``answer`` is ignored when declined.
   */
  onDeliverAnswer: (
    nodeId: string,
    answer: string,
    declined?: boolean,
  ) => Promise<boolean>;
}

// Sticky-redesign: the in-card answer textarea was removed -- answers
// are typed in the main chat input, which ``Chat.tsx`` routes through
// ``clarificationAnswerControl.onDeliverAnswer``.  The type export
// above is kept because the routing path still uses it.

const CUSTOM_INSTRUCTIONS_RE =
  /^\[CUSTOM INSTRUCTIONS\]\r?\n([\s\S]*?)\r?\n\[\/CUSTOM INSTRUCTIONS\]\r?\n\r?\n/;

const URGENT_THRESHOLD_SECS = 15;

// Prefers the BE-authoritative deadlineEpochMs over requestedAt + timeoutSecs so
// the timer can't drift on WS lag, tab-throttling, or browser-sleep.
const ClarificationCountdown: React.FC<{
  requestedAt: number;
  // null = "ask and wait": indefinite, no countdown.
  timeoutSecs: number | null;
  deadlineEpochMs?: number | null;
  isPaused?: boolean;
  // Kept for call-site compatibility; pausing hides the timer rather than
  // rendering a frozen value.
  pausedAtEpochMs?: number | null;
}> = ({ requestedAt, timeoutSecs, deadlineEpochMs, isPaused }) => {
  // "Ask and wait" parks the workflow indefinitely: no deadline to count to.
  const isIndefinite = timeoutSecs === null;
  // In the indefinite case ``timeoutSecs`` is null, so this collapses to
  // ``requestedAt`` — but the value is never shown: the effect below and the
  // component both early-return on ``isIndefinite``. It's computed
  // unconditionally only so the hooks (useState/useEffect) stay above the
  // early returns, keeping hook order stable across renders.
  const deadline = deadlineEpochMs ?? requestedAt + (timeoutSecs ?? 0) * 1000;
  const [remainingSecs, setRemainingSecs] = React.useState(() =>
    Math.max(0, Math.ceil((deadline - Date.now()) / 1000)),
  );
  React.useEffect(() => {
    // No interval when indefinite (nothing to tick) or while paused (the
    // timer is hidden and the BE's deadline is frozen too).
    if (isIndefinite || isPaused) return;
    setRemainingSecs(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    const interval = window.setInterval(() => {
      const next = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemainingSecs(next);
      if (next <= 0) window.clearInterval(interval);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [deadline, isPaused, isIndefinite]);

  // "Ask and wait": static label, no countdown. Placed AFTER the hooks
  // so hook order stays stable across renders.
  if (isIndefinite) {
    return (
      <span className='text-[11px] text-amber-700 dark:text-amber-300'>
        Waiting for your answer
      </span>
    );
  }

  // Hidden while paused (see the doc comment) -- placed AFTER the
  // hooks so hook order stays stable across renders.
  if (isPaused) return null;

  // Turn red + bold within this many seconds of the deadline.
  const urgent = remainingSecs <= URGENT_THRESHOLD_SECS && remainingSecs > 0;
  return (
    <span
      className={cn(
        'text-[11px] tabular-nums',
        urgent
          ? 'font-semibold text-red-600 dark:text-red-400'
          : 'text-amber-700 dark:text-amber-300',
      )}
    >
      {remainingSecs > 0 ? `Skipping in ${remainingSecs}s` : 'Skipping…'}
    </span>
  );
};

const EMPTY_NAMES: ReadonlyMap<string, string> = new Map();

export interface ChatMessageProps {
  message: ChatMessageData;
  /** Display names by reference key, for Data Collections tagged in a message. */
  collectionNames?: ReadonlyMap<string, string>;
  onApplyUpdates?: (messageId: string) => void;
  onConfirmRemoveNode?: (messageId: string) => void;
  onApplyWorkflowMutations?: (messageId: string) => void;
  // Disable Apply buttons on proposal/confirmation cards (locked
  // workflow), including cards from before the lock was applied.
  applyDisabled?: boolean;
  applyDisabledReason?: string;
  // Open artifact in the embedded right-side viewer
  onOpenArtifact?: (artifact: ArtifactListItem) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  collectionNames = EMPTY_NAMES,
  onApplyUpdates,
  onConfirmRemoveNode,
  onApplyWorkflowMutations,
  applyDisabled = false,
  applyDisabledReason,
  onOpenArtifact,
}) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isThinking = message.variant === 'thinking';
  const isDivider = message.variant === 'divider';
  const [copied, setCopied] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current !== null) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(message.content)
      .then(() => {
        setCopied(true);
        if (copyResetTimeoutRef.current !== null) {
          clearTimeout(copyResetTimeoutRef.current);
        }
        copyResetTimeoutRef.current = setTimeout(() => {
          copyResetTimeoutRef.current = null;
          setCopied(false);
        }, 2000);
      })
      .catch(() => {
        console.warn('Clipboard write failed — access denied or unavailable');
      });
  }, [message.content]);

  const { cleanContent, instructionsText, taggedCollections } = useMemo(() => {
    if (!isUser) {
      return {
        cleanContent: message.content,
        instructionsText: undefined,
        taggedCollections: [],
      };
    }
    const match = message.content.match(CUSTOM_INSTRUCTIONS_RE);
    const extracted = match ? match[1].trim() : undefined;
    const withoutInstructions = match
      ? message.content.replace(CUSTOM_INSTRUCTIONS_RE, '')
      : message.content;
    // Live sends carry the references on the message; reloaded ones only
    // have the tag block platform-api appended to the text.
    const parsed = parseCollectionTags(withoutInstructions);
    return {
      cleanContent: parsed.cleanContent,
      instructionsText: extracted || message.customInstructions || undefined,
      taggedCollections: message.collections?.length
        ? message.collections
        : parsed.references,
    };
  }, [
    message.content,
    message.customInstructions,
    message.collections,
    isUser,
  ]);

  const renderContent = () => {
    const textClassName = cn(
      'leading-normal',
      isThinking
        ? 'text-xs text-zinc-500 dark:text-zinc-400 italic'
        : isDivider
          ? 'text-xs text-zinc-400 dark:text-zinc-500'
          : 'text-sm',
    );

    const displayContent = isThinking
      ? `Thinking: ${message.content}`
      : cleanContent;

    if (isAssistant || message.isStreaming) {
      return (
        <StreamingMarkdownText
          content={displayContent}
          isStreaming={message.isStreaming}
          uniqueKey={message.id}
          className={textClassName}
        />
      );
    }

    return (
      <div className={cn('whitespace-pre-wrap', textClassName)}>
        {displayContent}
      </div>
    );
  };

  if (message.variant === 'node_update' && message.pendingUpdates) {
    return (
      <div className='flex flex-col gap-1'>
        <NodeUpdateMessage
          updates={message.pendingUpdates}
          applied={!!message.updatesApplied}
          onApply={() => onApplyUpdates?.(message.id)}
          applyDisabled={applyDisabled}
          applyDisabledReason={applyDisabledReason}
        />
      </div>
    );
  }

  // Chat.tsx already renders these through ClarificationBatchCard; rendering
  // here too would duplicate the card.
  if (message.variant === 'clarification_request') {
    return null;
  }

  if (
    message.variant === 'workflow_mutations_confirm' &&
    message.pendingWorkflowMutations?.length
  ) {
    return (
      <div className='flex flex-col gap-1'>
        <WorkflowMutationMessage
          mutations={message.pendingWorkflowMutations}
          applied={!!message.workflowMutationsApplied}
          onApply={() => onApplyWorkflowMutations?.(message.id)}
          applyDisabled={applyDisabled}
          applyDisabledReason={applyDisabledReason}
        />
      </div>
    );
  }

  if (
    message.variant === 'remove_node_confirm' &&
    message.pendingRemoveNodes?.length
  ) {
    const nodes = message.pendingRemoveNodes;
    const confirmed = !!message.removalsConfirmed;
    const count = nodes.length;
    const summary =
      count === 1 ? `Remove ${nodes[0].nodeName}` : `Remove ${count} nodes`;

    return (
      <div className='rounded-xl border border-border bg-zinc-50 dark:bg-zinc-900'>
        <div className='flex items-center gap-2 px-3 py-2'>
          <div className='flex items-center gap-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-400'>
            <Trash2 className='size-3.5 text-red-500' />
            <span>{summary}</span>
          </div>
          <div className='flex-1' />
          {confirmed ? (
            <span className='flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400'>
              <Check className='size-3.5' />
              Removed
            </span>
          ) : (
            <button
              type='button'
              onClick={() => onConfirmRemoveNode?.(message.id)}
              disabled={applyDisabled}
              title={applyDisabled ? applyDisabledReason : undefined}
              className='rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-600'
            >
              Confirm
            </button>
          )}
        </div>
        {count > 1 && (
          <div className='border-t border-border px-3 pb-2 pt-1.5'>
            <ul className='space-y-0.5'>
              {nodes.map((n) => (
                <li
                  key={n.instanceId}
                  className='text-xs text-zinc-500 dark:text-zinc-400'
                >
                  • {n.nodeName}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (
    message.variant === 'workflow_progress' &&
    message.progressActions?.length
  ) {
    const actions = message.progressActions;
    const done = !!message.progressComplete;

    return (
      <div className='rounded-xl border border-border bg-zinc-50 dark:bg-zinc-900'>
        <div className='flex items-center gap-2 px-3 py-2'>
          <div className='flex items-center gap-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-400'>
            {done ? (
              <Wrench className='size-3.5' />
            ) : (
              <Loader2 className='size-3.5 animate-spin' />
            )}
            <span>
              {done
                ? `${actions.length} action${actions.length !== 1 ? 's' : ''} planned`
                : `Planning workflow... (${actions.length})`}
            </span>
          </div>
        </div>
        <div className='border-t border-border px-3 pb-2 pt-1.5'>
          <ul className='space-y-0.5'>
            {actions.map((a, i) => (
              <li
                key={i}
                className='flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400'
              >
                <span className='size-1 shrink-0 rounded-full bg-zinc-400 dark:bg-zinc-500' />
                {a.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  if (message.variant === 'artifacts') {
    const artifacts = message.artifacts ?? [];
    const fallbackFiles = message.fallbackFiles ?? [];

    if (artifacts.length === 0 && fallbackFiles.length === 0) return null;

    if (artifacts.length > 0) {
      return (
        <div className='flex flex-col gap-2 py-1'>
          <ChatArtifactCards
            cards={promotedArtifactCards(artifacts)}
            onOpen={onOpenArtifact}
          />
        </div>
      );
    }

    // Fallback: canvas terminal files when promotion timed out
    return (
      <div className='flex flex-col gap-1.5 py-1'>
        {fallbackFiles.map((f, i) => {
          const FileIcon = getFileIcon(f.name);
          return (
            <div
              key={i}
              className='flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2'
            >
              <FileIcon className='size-4 shrink-0 text-muted-foreground' />
              <span className='min-w-0 flex-1 truncate text-xs text-foreground'>
                {f.name}
              </span>
              <span className='shrink-0 text-[10px] text-muted-foreground'>
                Processing…
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  if (isUser) {
    return (
      <div className='flex flex-col items-end gap-1'>
        {cleanContent.trim().length > 0 && (
          <div className='max-w-[85%] rounded-2xl bg-zinc-100 px-3 py-2 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 [&_*]:text-inherit [&_a]:underline'>
            {renderContent()}
          </div>
        )}
        <CollectionChips
          references={taggedCollections}
          names={collectionNames}
        />
        {instructionsText && (
          <div className='flex flex-col items-end gap-0.5'>
            <button
              type='button'
              onClick={() => setShowInstructions((v) => !v)}
              className='flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors'
            >
              <Cog className='size-2.5' />
              Custom instructions applied
              {showInstructions ? (
                <ChevronUp className='size-2.5' />
              ) : (
                <ChevronDown className='size-2.5' />
              )}
            </button>
            {showInstructions && (
              <div className='max-w-[85%] rounded-lg bg-muted/50 px-2.5 py-1.5 text-[11px] text-muted-foreground whitespace-pre-wrap'>
                {instructionsText}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group relative rounded-lg transition-colors',
        isAssistant &&
          !isThinking &&
          !isDivider &&
          'hover:bg-zinc-50 dark:hover:bg-zinc-900/50 -mx-2 px-2 -my-1 py-1',
      )}
    >
      {isAssistant && message.toolCalls && message.toolCalls.length > 0 && (
        <ToolCallRollup
          events={message.toolCalls}
          className={cn(message.content.length > 0 && 'mb-2')}
        />
      )}
      <div
        className={cn(
          'select-text leading-normal',
          isThinking
            ? 'text-xs text-zinc-500 dark:text-zinc-400'
            : isDivider
              ? 'text-xs text-zinc-400 dark:text-zinc-500'
              : 'text-sm text-zinc-900 dark:text-zinc-100',
        )}
      >
        {renderContent()}
      </div>
      {isAssistant &&
        !isThinking &&
        !isDivider &&
        message.content.length > 0 && (
          <button
            onClick={handleCopy}
            className='absolute right-2 top-2 flex items-center gap-1 rounded bg-white px-1.5 py-1 text-xs shadow-sm opacity-0 transition-opacity group-hover:opacity-100 text-zinc-400 hover:text-zinc-600 dark:bg-zinc-800 dark:hover:text-zinc-300'
          >
            {copied ? (
              <>
                <Check className='h-3 w-3' />
                Copied
              </>
            ) : (
              <Copy className='h-3 w-3' />
            )}
          </button>
        )}
    </div>
  );
};

export default ChatMessage;

// Clarifications from one DAG level share a workflow-global deadline and pause
// state, so they get one header + N body segments instead of N countdowns.
export const ClarificationBatchCard: React.FC<{
  /** All same-batch messages, in chat order.  ``length >= 1``. */
  messages: ChatMessageData[];
  /**
   * Live node-name resolver so the header shows the asking node's
   * *current* canvas label (post-rename) instead of the BE snapshot.
   * Falls back to the snapshot when undefined or the node is deleted.
   */
  resolveNodeName?: (nodeId: string) => string | undefined;
  /**
   * Resolves the asking node's icon (before the "<node> asked:" header)
   * from the live node store.  ``undefined`` (e.g. deleted node) falls
   * back to a question-mark.
   */
  resolveNodeIcon?: (nodeId: string) => React.ReactNode | undefined;
  /**
   * Click-to-focus handler for the "<node> asked:" header; makes the
   * node name a button that pans/zooms the canvas to the asking node.
   */
  onFocusNode?: (nodeId: string) => void;
}> = ({ messages, resolveNodeName, resolveNodeIcon, onFocusNode }) => {
  if (!messages[0]?.clarificationRequest) return null;

  // Sticky-redesign: this transcript card is the RESOLVED-only path
  // (pending clarifications hoist to ``ClarificationStickyCard``).
  // Each segment carries its own "<node> asked:" header + divider so
  // the user can see WHICH node asked WHICH question in a batch.
  // Pending-segment branches are tolerated defensively but normally
  // filtered out upstream in ``Chat.tsx``.
  return (
    <div className='rounded-xl border border-zinc-200 dark:border-zinc-800'>
      <div className='space-y-4 p-3'>
        {messages.map((m) => {
          const c = m.clarificationRequest;
          if (!c) return null;
          const nodeName = resolveNodeName?.(c.nodeId) || c.nodeDisplayName;
          const resolution = m.clarificationResolution;
          const segmentAnswered = resolution === 'answered';
          const segmentTimedOut = resolution === 'timed_out';
          const segmentSkipped = resolution === 'skipped';
          const segmentAbandoned = resolution === 'abandoned';
          return (
            <div key={m.id}>
              {/* Segment header mirrors the active sticky card: node
                  icon + bolded node name + a faded "asked:" suffix. */}
              <div className='flex items-center gap-1.5 pb-1.5 text-xs text-zinc-700 dark:text-zinc-300'>
                <span className='flex size-3.5 shrink-0 items-center justify-center'>
                  {resolveNodeIcon?.(c.nodeId) ?? (
                    <HelpCircle className='size-3.5 text-zinc-400 dark:text-zinc-500' />
                  )}
                </span>
                {onFocusNode ? (
                  <button
                    type='button'
                    onClick={() => onFocusNode(c.nodeId)}
                    className={cn(
                      '-mx-0.5 rounded px-0.5 font-medium',
                      'hover:bg-zinc-100 hover:text-zinc-900',
                      'dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
                    )}
                    title={`Focus ${nodeName} on canvas`}
                  >
                    {nodeName}
                  </button>
                ) : (
                  <span className='font-medium'>{nodeName}</span>
                )}
                <span className='font-normal text-zinc-500 dark:text-zinc-400'>
                  asked:
                </span>
              </div>
              <div className='border-t border-zinc-200 dark:border-zinc-800' />
              <div className='mt-2 whitespace-pre-wrap text-xs text-zinc-500 dark:text-zinc-400'>
                {c.question}
              </div>
              {segmentAnswered && c.userAnswer ? (
                <div className='mt-1 whitespace-pre-wrap text-xs text-zinc-900 dark:text-zinc-100'>
                  {c.userAnswer}
                </div>
              ) : null}
              {segmentTimedOut ? (
                <div className='mt-1 text-xs italic text-zinc-400 dark:text-zinc-500'>
                  No answer (timed out)
                </div>
              ) : null}
              {segmentSkipped ? (
                <div className='mt-1 text-xs italic text-zinc-400 dark:text-zinc-500'>
                  Skipped
                </div>
              ) : null}
              {segmentAbandoned ? (
                <div className='mt-1 text-xs italic text-zinc-400 dark:text-zinc-500'>
                  Skipped (no answer — run stopped)
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Controlled: pagination and answer routing live in Chat.tsx because the chat
// input below doubles as the answer field and needs the parent-owned index.
export const ClarificationStickyCard: React.FC<{
  /** All currently-pending clarification messages, in chat order. */
  messages: ChatMessageData[];
  /** Parent-owned index into ``messages``; clamped by the parent. */
  currentIndex: number;
  /** Direct index setter (used by chevron pagination). */
  onCurrentIndexChange: (idx: number) => void;
  /**
   * Skip = resolve the current question as "user declined" (same
   * ``onDeliverAnswer`` route as a typed answer, with the ``declined``
   * flag).  See ``handleSkipCurrent`` in ``Chat.tsx``.
   */
  onSkipCurrent: () => void;
  onFocusNode?: (nodeId: string) => void;
  resolveNodeName?: (nodeId: string) => string | undefined;
  /**
   * Resolves the asking node's icon for the card header from the live
   * node store.  ``undefined`` (e.g. deleted node) falls back to a
   * question-mark.
   */
  resolveNodeIcon?: (nodeId: string) => React.ReactNode | undefined;
  /** Hover-pause notifier; fires true on enter, false on leave. */
  onHoverChange?: (hovering: boolean) => void;
}> = ({
  messages,
  currentIndex,
  onCurrentIndexChange,
  onSkipCurrent,
  onFocusNode,
  resolveNodeName,
  resolveNodeIcon,
  onHoverChange,
}) => {
  const totalCount = messages.length;
  if (totalCount === 0) return null;
  const safeIndex = Math.min(Math.max(currentIndex, 0), totalCount - 1);
  const current = messages[safeIndex];
  if (!current?.clarificationRequest) return null;
  const c = current.clarificationRequest;

  const nameFor = (nodeId: string, fallback: string): string =>
    resolveNodeName?.(nodeId) || fallback;
  const segmentName = nameFor(c.nodeId, c.nodeDisplayName);

  const canPrev = safeIndex > 0;
  const canNext = safeIndex < totalCount - 1;
  const showPagination = totalCount > 1;

  const navButtonCls = cn(
    'flex h-5 w-5 items-center justify-center rounded',
    'border border-zinc-200 text-zinc-500 transition-colors',
    'hover:bg-zinc-100 hover:text-zinc-700',
    'dark:border-zinc-700 dark:text-zinc-400',
    'dark:hover:bg-zinc-800 dark:hover:text-zinc-200',
    'disabled:cursor-not-allowed disabled:opacity-40',
    'disabled:hover:bg-transparent disabled:hover:text-zinc-500',
    'dark:disabled:hover:bg-transparent dark:disabled:hover:text-zinc-400',
  );

  // Header is "<node_name> has a question": the name truncates,
  // " has a question" stays visible (shrink-0).  With a focus handler
  // the name is a button (hover highlight + tooltip + click to pan).
  const headerNameNode = (
    <span className='flex min-w-0 flex-1 items-baseline gap-1'>
      {onFocusNode ? (
        <button
          type='button'
          onClick={() => onFocusNode(c.nodeId)}
          className={cn(
            'min-w-0 truncate rounded px-0.5 -mx-0.5',
            'hover:bg-zinc-100 hover:text-zinc-900',
            'dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
          )}
          title={`Focus ${segmentName} on canvas`}
        >
          {segmentName}
        </button>
      ) : (
        <span className='min-w-0 truncate'>{segmentName}</span>
      )}
      <span className='shrink-0 font-normal text-zinc-500 dark:text-zinc-400'>
        has a question
      </span>
    </span>
  );

  // Bottom-left paginator (chevrons + ``N/M``) so the user sees queue
  // position at a glance.  ``shrink-0`` so it holds its size as the card
  // narrows.
  const paginationNode = showPagination ? (
    <div className='flex shrink-0 items-center gap-1.5 text-[11px] text-zinc-500 dark:text-zinc-400'>
      <button
        type='button'
        onClick={() => onCurrentIndexChange(safeIndex - 1)}
        disabled={!canPrev}
        aria-label='Previous question'
        className={navButtonCls}
      >
        <ChevronLeft className='size-3' />
      </button>
      <span className='tabular-nums'>
        {safeIndex + 1}/{totalCount}
      </span>
      <button
        type='button'
        onClick={() => onCurrentIndexChange(safeIndex + 1)}
        disabled={!canNext}
        aria-label='Next question'
        className={navButtonCls}
      >
        <ChevronRight className='size-3' />
      </button>
    </div>
  ) : null;

  // Sticky-redesign: the card visually "pops up" from the input
  // below -- narrower (``px-8`` vs ``px-4``), matching ``bg-sidebar``,
  // and bottom-less (``rounded-t-xl border border-b-0``) so its bottom
  // edge continues into the input, with an amber top/left/right border.
  return (
    <div
      className={cn(
        'rounded-t-xl border border-b-0 border-amber-300 bg-sidebar',
        'dark:border-amber-700/60',
      )}
      onMouseEnter={onHoverChange ? () => onHoverChange(true) : undefined}
      onMouseLeave={onHoverChange ? () => onHoverChange(false) : undefined}
    >
      <div className='flex items-center gap-2 border-b border-amber-200/60 px-3 py-2 dark:border-amber-800/40'>
        {/* Asking node's own icon (from the node store); falls back to
            a question-mark when the node isn't on the canvas. */}
        <span className='flex size-3.5 shrink-0 items-center justify-center'>
          {resolveNodeIcon?.(c.nodeId) ?? (
            <HelpCircle className='size-3.5 text-amber-600 dark:text-amber-400' />
          )}
        </span>
        <div className='flex min-w-0 flex-1 items-baseline gap-1 text-xs font-medium text-zinc-700 dark:text-zinc-300'>
          {headerNameNode}
        </div>
      </div>
      <div className='px-3 pb-2 pt-2'>
        <div className='whitespace-pre-wrap text-xs text-zinc-900 dark:text-zinc-100'>
          {c.question}
        </div>
        {/* c.context (the LLM's reasoning for asking) is deliberately not
            rendered -- it muddies what the user is being asked. */}
        {/* Surface a drafted answer so paging back makes clear that typing in
            the chat input will replace it. */}
        {c.submitted ? (
          <div className='mt-1.5 flex items-baseline gap-1.5 text-[11px] text-zinc-500 dark:text-zinc-400'>
            <span className='shrink-0 font-medium'>
              {c.submittedDeclined ? 'Skipped' : 'Your answer:'}
            </span>
            {c.submittedDeclined ? null : (
              <span className='min-w-0 truncate italic text-zinc-600 dark:text-zinc-300'>
                {c.userAnswer}
              </span>
            )}
          </div>
        ) : null}
        {/* Bottom control row: paginator on the left, countdown + pause +
            Skip pushed to the right (``ml-auto``).  The answer is
            typed/sent in the chat input below. */}
        <div className='mt-2 flex items-center gap-3'>
          {paginationNode}
          <div className='ml-auto flex items-center gap-3'>
            <ClarificationCountdown
              requestedAt={c.requestedAt}
              timeoutSecs={c.timeoutSecs}
              deadlineEpochMs={c.deadlineEpochMs}
              isPaused={c.isPaused}
              pausedAtEpochMs={c.pausedAtEpochMs}
            />
            {/* Skip drafts the current question with the ``declined``
                flag (empty answer; see ``handleSkipCurrent``), so the
                transcript reads "Skipped".  A revisable draft like any
                answer until the batch commits. */}
            <button
              type='button'
              onClick={onSkipCurrent}
              className={cn(
                'rounded-md border border-amber-300 px-2 py-0.5 text-[11px]',
                'font-medium text-amber-700 transition-colors',
                'hover:bg-amber-50 hover:text-amber-800',
                'dark:border-amber-700/60 dark:text-amber-300',
                'dark:hover:bg-amber-950/40 dark:hover:text-amber-200',
              )}
              title='Skip this question; the asking node will proceed without your input'
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
